// src/view/admin/did/components/FilterBar.jsx

import {
  Paper,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import { Plus, RefreshCw, Search as SearchIcon, X } from "lucide-react";

export const FilterBar = ({
  searchTerm,
  onSearchChange,
  countryFilter,
  onCountryChange,
  availabilityFilter,
  onAvailabilityChange,
  countries,
  filteredCount,
  totalCount,
  onClearFilters,
  openAddForm,
  handleRefresh,
}) => {
  const hasActiveFilters =
    searchTerm || countryFilter !== "all" || availabilityFilter !== "all";

  const role = localStorage.getItem("role");

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        background:
          "linear-gradient(135deg, #f9fafb 0%, #ffffff 60%, #eff6ff 100%)",
        boxShadow: "0 4px 10px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Stack spacing={2}>
        {/* Top row: filters on left, actions on right */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          {/* Filters group */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            flex={1}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search by DID or country..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} color="#94a3b8" />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f9fafb",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.15)",
                  },
                },
              }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={countryFilter}
                onChange={(e) => onCountryChange(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#f9fafb",
                  "& .MuiSelect-select": { py: 1.1 },
                }}
              >
                <MenuItem value="all">All Countries</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={availabilityFilter}
                onChange={(e) => onAvailabilityChange(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#f9fafb",
                  "& .MuiSelect-select": { py: 1.1 },
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Actions group */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={openAddForm}
              disabled={role !== "admin" && role !== "superadmin"}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                py: 0.8,
                bgcolor: "#6366F1",
                "&:hover": {
                  bgcolor: "#1d4ed8",
                },
              }}
            >
              Add DID
            </Button>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              sx={{
                borderRadius: 2,
                minWidth: 44,
                px: 1.5,
                py: 0.8,
                borderColor: "#6a6a6aff",
                color: "#4b5563",
                bgcolor: "white",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              <RefreshCw size={18} />
            </Button>
          </Stack>
        </Stack>

        {/* Filter info / clear line */}
        {hasActiveFilters && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Showing{" "}
              <Typography component="span" fontWeight={600}>
                {filteredCount}
              </Typography>{" "}
              of{" "}
              <Typography component="span" fontWeight={600}>
                {totalCount}
              </Typography>{" "}
              DIDs
            </Typography>

            <Chip
              size="small"
              label="Clear filters"
              onDelete={onClearFilters}
              deleteIcon={<X size={14} />}
              sx={{
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                bgcolor: "#fef2f2",
                color: "#b91c1c",
                "& .MuiChip-deleteIcon": {
                  color: "#b91c1c",
                },
                "&:hover": {
                  bgcolor: "#fee2e2",
                },
              }}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
